import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchPatientFormValues } from 'src/components/SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

import { Api } from 'src/models/Api.model';
import {
  CriteriaGroupGroupElementsInner,
  CriteriaGroupGroupElementsInnerOperatorEnum,
} from 'src/models/GalenData/CriteriaGroupGroupElementsInner.model';
import { CriteriaGroupXGroupElementsInnerOperatorEnum } from 'src/models/GalenData/CriteriaGroupXGroupElementsInner.model';
import { EntityCriteriaOperatorEnum } from 'src/models/GalenData/EntityCriteria.model';
import { OrderData, OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { PageOfDeviceDataView } from 'src/models/GalenData/PageOfDeviceDataView.model';
import { PageOfUser } from 'src/models/GalenData/PageOfUser.model';
import { SortOrderEnum } from 'src/models/GalenData/SortOrder.model';
import { I18NextNS } from 'src/models/Language.model';

import {
  getPatientFilterConditions,
  sanitizeSearchPatientValues,
} from 'src/utils/patientFilters';

import useHandleHttpRequestError from './useHandleHttpRequestError';

const DEFAULT_PAGE_SIZE = 10;

export const useGetFilterOrder = () => {
  const [ordersData, setOrdersData] =
    useState<PageOfDeviceDataView<OrderData>>();
  const [isGettingOrders, setIsGettingOrders] = useState(false);

  const { handleHttpRequestError } = useHandleHttpRequestError();
  const {
    i18n: { language },
    t,
  } = useTranslation(I18NextNS.COMMON);

  const getSortingParams = useCallback((status: OrderStatusEnum[]) => {
    return status.includes(OrderStatusEnum.REVIEW)
      ? {
          sortBy: ['data.Priority.value', 'data.ReportDatetime.value'],
          sortOrder: SortOrderEnum.ASC,
        }
      : {
          sortBy: ['minValueProvidedOn'],
          sortOrder: SortOrderEnum.DESC,
        };
  }, []);

  const fetchOrdersCore = useCallback(
    async ({
      status,
      pageNumber,
      pageSize,
      siteId,
      patientsData,
    }: {
      status: OrderStatusEnum[];
      pageNumber: number;
      pageSize: number;
      siteId?: string;
      patientsData?: PageOfUser;
    }): Promise<PageOfDeviceDataView<OrderData> | undefined> => {
      const { sortBy, sortOrder } = getSortingParams(status);

      if (patientsData?.totalElements === 0) {
        const emptyResult: PageOfDeviceDataView<OrderData> = {
          content: [],
          totalElements: 0,
          totalPages: 0,
        };
        setOrdersData(emptyResult);
        return emptyResult;
      }

      const groupElements: CriteriaGroupGroupElementsInner[] = [
        {
          groupElements: status.map((status) => ({
            key: 'Status',
            value: status,
            operator: CriteriaGroupXGroupElementsInnerOperatorEnum.Equal,
          })),
          isOr: true,
        },
        ...(siteId
          ? [
              {
                key: 'SiteId',
                value: siteId,
                operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
              },
            ]
          : []),
      ];

      const ordersPage = await Api.remote.get_exam_order({
        groupElements,
        ownerFilter: {
          users: patientsData?.content?.map((patient) => patient.userId),
        },
        params: {
          pageNumber,
          pageSize,
          sortBy,
          sortOrder,
        },
      });

      const result = {
        content: ordersPage.data.content,
        totalElements: ordersPage.data.totalElements,
        totalPages: ordersPage.data.totalPages,
      };

      setOrdersData(result);
      return result;
    },
    [getSortingParams],
  );

  const getFilteredOrdersCore = useCallback(
    async ({
      siteId,
      pageNumber,
      searchValues,
      status,
      pageSize,
    }: {
      siteId: string | undefined;
      pageNumber: number;
      searchValues?: SearchPatientFormValues;
      status: OrderStatusEnum[];
      pageSize: number;
    }): Promise<PageOfDeviceDataView<OrderData> | undefined> => {
      const sanitizedSearchValues = sanitizeSearchPatientValues(searchValues);

      if (sanitizedSearchValues.length === 0) {
        return await fetchOrdersCore({
          status,
          pageNumber,
          pageSize,
          siteId,
        });
      }

      const patientFiltersWithSiteId = [
        ...getPatientFilterConditions(sanitizedSearchValues),
        ...(siteId
          ? [
              {
                key: import.meta.env.VITE_USER_SITES_FIELD_ID,
                value: `\\"${siteId}\\"`,
                operator: EntityCriteriaOperatorEnum.Like,
                isCustomField: true,
                isDataValue: false,
              },
            ]
          : []),
      ];

      const patientsData = await Api.remote._get_users_advanced(
        patientFiltersWithSiteId,
        {
          pageNumber: 0,
          sortBy: ['createdOn'],
          sortOrder: SortOrderEnum.DESC,
        },
      );

      return await fetchOrdersCore({
        status,
        pageNumber,
        pageSize,
        siteId,
        patientsData: patientsData.data,
      });
    },
    [fetchOrdersCore],
  );

  const getFilteredOrders = useCallback(
    async ({
      siteId,
      pageNumber,
      searchValues,
      status = [OrderStatusEnum.OPEN],
      pageSize = DEFAULT_PAGE_SIZE,
    }: {
      siteId: string | undefined;
      pageNumber: number;
      searchValues?: SearchPatientFormValues;
      status?: OrderStatusEnum[];
      pageSize?: number;
    }) => {
      try {
        setIsGettingOrders(true);
        await getFilteredOrdersCore({
          siteId,
          pageNumber,
          pageSize,
          searchValues,
          status,
        });
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: t('bootbox.title.Error'),
          message: errorMessage,
          locale: language,
          type: 'error',
        });
      } finally {
        setIsGettingOrders(false);
      }
    },
    [getFilteredOrdersCore, handleHttpRequestError, language, t],
  );

  return {
    getFilteredOrders,
    getFilteredOrdersCore,
    ordersData,
    isGettingOrders,
  };
};
